/**
 * Voci di navigazione DietaGest.
 *
 * Sorgente UNICA per le sezioni di navigazione: sidebar desktop (8 voci,
 * design.md §9.4) e bottom tab bar mobile (5 voci, §9.5 + 4 voci
 * secondarie nell'overlay "Altro").
 *
 * Regola inviolabile: l'ORDINE e i NOMI di queste voci sono fissati dal
 * design e NON vanno modificati senza prima aggiornare design.md.
 *
 * Le icone sono Material Symbols Outlined (vedi rule ui-design-system.mdc).
 *
 * RBAC nav (PR-G3a): `allowedRoles` e' opzionale. Se assente, la voce e'
 * visibile a ogni ruolo autenticato (comportamento pre-G3a). Valorizzato
 * SOLO sulla voce Piani Alimentari — nessun'altra voce deve filtrare.
 */

import type { UserRole } from '@shared/types/auth';

export interface NavItem {
  /** Path da passare a react-router `<NavLink to>`. */
  to: string;
  /** Etichetta italiana. */
  label: string;
  /** Nome icona Material Symbols Outlined. */
  icon: string;
  /** Se true, NavLink usa `end` per match esatto (default per "/app"). */
  exact?: boolean;
  /**
   * Se presente, la voce e' visibile solo per questi ruoli.
   * Assente = visibile a tutti (nessun filtro).
   */
  allowedRoles?: ReadonlyArray<UserRole>;
}

/** Ruoli ammessi alla libreria template (allineato a RouteGuard /app/piani). */
export const PIANI_NAV_ROLES: ReadonlyArray<UserRole> = [
  'TITOLARE',
  'DIETOLOGA',
  'SEGRETERIA',
  'ADMIN',
];

/**
 * Sidebar desktop, 8 voci §9.4 — ordine fisso.
 * Solo "Piani Alimentari" ha `allowedRoles`.
 */
export const SIDEBAR_NAV: ReadonlyArray<NavItem> = [
  { to: '/app', label: 'Home', icon: 'dashboard', exact: true },
  { to: '/app/agenda', label: 'Agenda', icon: 'calendar_today' },
  { to: '/app/pazienti', label: 'Pazienti', icon: 'group' },
  {
    to: '/app/piani',
    label: 'Piani Alimentari',
    icon: 'restaurant_menu',
    allowedRoles: PIANI_NAV_ROLES,
  },
  { to: '/app/fatturazione', label: 'Fatturazione', icon: 'receipt_long' },
  { to: '/app/comunicazioni', label: 'Comunicazioni', icon: 'chat' },
  { to: '/app/statistiche', label: 'Statistiche', icon: 'monitoring' },
  { to: '/app/impostazioni', label: 'Impostazioni', icon: 'settings' },
] as const;

/**
 * Bottom tab bar mobile, 5 voci §9.5 — ordine fisso.
 * La quinta voce "Altro" non naviga: apre un BottomSheet con MORE_TAB_NAV.
 */
export const BOTTOM_TAB_NAV: ReadonlyArray<
  NavItem | { label: string; icon: string; action: 'more' }
> = [
  { to: '/app', label: 'Home', icon: 'dashboard', exact: true },
  { to: '/app/agenda', label: 'Agenda', icon: 'calendar_today' },
  { to: '/app/pazienti', label: 'Pazienti', icon: 'group' },
  { to: '/app/fatturazione', label: 'Fatture', icon: 'receipt_long' },
  { label: 'Altro', icon: 'menu', action: 'more' },
] as const;

/**
 * Voci secondarie nell'overlay "Altro" del bottom tab — 4 voci.
 * Solo "Piani Alimentari" ha `allowedRoles`.
 */
export const MORE_TAB_NAV: ReadonlyArray<NavItem> = [
  {
    to: '/app/piani',
    label: 'Piani Alimentari',
    icon: 'restaurant_menu',
    allowedRoles: PIANI_NAV_ROLES,
  },
  { to: '/app/comunicazioni', label: 'Comunicazioni', icon: 'chat' },
  { to: '/app/statistiche', label: 'Statistiche', icon: 'monitoring' },
  { to: '/app/impostazioni', label: 'Impostazioni', icon: 'settings' },
] as const;

/** True se la voce va mostrata per il ruolo corrente. */
export function isNavItemVisibleForRole(
  item: NavItem,
  role: UserRole | null
): boolean {
  if (!item.allowedRoles || item.allowedRoles.length === 0) return true;
  if (!role) return false;
  return item.allowedRoles.includes(role);
}

export function filterNavItemsForRole(
  items: ReadonlyArray<NavItem>,
  role: UserRole | null
): NavItem[] {
  return items.filter((item) => isNavItemVisibleForRole(item, role));
}
